import React from 'react';
import { Plus, X, Boxes } from 'lucide-react';
import { UNIT_LABEL } from '../pages/ServiceEditor/create/serviceCreateTaxonomy';

/**
 * SAM-546 (N11) — N-resources editor. New Tailwind component (BaseUI/Styletron
 * freeze, SAM-175) mounted from ServiceDetailDrawer.tsx with a minimal touch,
 * same pattern as ServicePricingMatrix.tsx.
 *
 * Maps 1:1 to `service.settings.resources` (SAM-540, `functions/booking/engine_sync.py`
 * `_normalize_resource_configs`) — ONLY the three fields the backend actually
 * reads (name, capacity, max_simultaneous_bookings) plus the stable `key` the
 * sync uses to recognize a resource across saves. Nothing else here reaches
 * the engine, on purpose: price lives in Booking Options / Role Pricing
 * above, never here (per SAM-537 "el precio vive en SAM; la disponibilidad
 * vive en el motor").
 *
 * Empty list = the pre-SAM-546 single-resource behaviour, unchanged: the
 * service's own Max Capacity / Max Per Slot (or Daily Capacity) fields above
 * still describe that one implicit resource. Adding a resource here is
 * opt-in — a service is never forced into the multi-resource shape.
 */

export interface ResourceForm {
  /** Stable once set — see the SAM-546 note on ServiceResourceConfig.key. Never edited by hand. */
  key: string;
  name: string;
  capacity: string;
  max_simultaneous_bookings: string;
  /** SAM-591 — per-unit override of pricing.unit (settings.resources[].metadata). '' = use the
   * service default. Optional so rows built before this ticket stay valid. */
  price?: string;
  included_pax?: string;
  max_pax?: string;
  /** Any other metadata keys on the stored resource, round-tripped untouched. */
  metadata_rest?: Record<string, unknown>;
}

export type ResourceFormField =
  'name' | 'capacity' | 'max_simultaneous_bookings' | 'price' | 'included_pax' | 'max_pax';

function slugify(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || 'resource';
}

/** Guarantees a unique key among the resources already in the list — mirrors the backend's own
 * duplicate-key rejection (`EngineSyncError: Duplicate resource key ...`) by never producing one
 * client-side in the first place. */
export function uniqueResourceKey(name: string, existing: ResourceForm[]): string {
  const base = slugify(name);
  let candidate = base;
  let i = 2;
  while (existing.some((r) => r.key === candidate)) {
    candidate = `${base}_${i}`;
    i += 1;
  }
  return candidate;
}

// SAM-546 follow-up (SAM-198 catalog work) — "resumen vivo" for the unit list, e.g.
// "5 cabañas · 34 pax max". Subtype-aware only for the one subtype the ticket names
// ('cabana'); any other subtype/custom service reads as generic "resources" — there is no
// stored per-resource label to draw from (`settings.resources[]` only has name/capacity/
// max_simultaneous_bookings, see engine_sync.py `_build_resource_payload`), so this is a
// presentation-only word choice, not a field the backend needs to agree with.
const RESOURCE_TYPE_LABELS: Record<string, { singular: string; plural: string }> = {
  cabana: { singular: 'cabaña', plural: 'cabañas' },
};

export function resourceTypeLabel(subtype: string, count: number): string {
  const labels = RESOURCE_TYPE_LABELS[subtype] || { singular: 'resource', plural: 'resources' };
  return count === 1 ? labels.singular : labels.plural;
}

/** Prototype's `capWord` — what the capacity number actually counts, per subtype. */
export function unitCapacityWord(subtype: string): string {
  return subtype === 'teetime' ? 'Players' : 'Max guests';
}

/** "5 cabañas · 34 pax max" — total unit count and summed per-unit capacity. Capacity is summed
 * only from rows that actually set one; a resource left blank contributes 0 rather than making the
 * whole total unknown (unlike ServiceEffectivePreview's aforo card, which needs a stricter
 * all-or-nothing "—" for the venue capacity math). Returns null for an empty list — the caller
 * already has a distinct empty-state message. */
export function summarizeResources(resources: ResourceForm[], subtype: string): string | null {
  if (resources.length === 0) return null;
  const count = resources.length;
  const label = resourceTypeLabel(subtype, count);
  const totalPax = resources.reduce((sum, r) => sum + (Number(r.capacity) || 0), 0);
  return `${count} ${label} · ${totalPax} pax max`;
}

interface ServiceResourcesEditorProps {
  resources: ResourceForm[];
  bookingType: 'fixed' | 'day' | 'flexible';
  /** '' | ServiceSubtype — kept as a plain string here (not imported from useServices) so this
   * leaf component has no dependency on the editor's service types. */
  subtype?: string;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, field: ResourceFormField, value: string) => void;
  /** SAM-591 — true when the service is unit-priced: shows the per-unit price / included / max
   * pax override fields. `unitDefaults` are shown as placeholders (the service-level values). */
  unitPricing?: boolean;
  unitDefaults?: { price: string; included_pax: string; max_pax: string };
}

export const ServiceResourcesEditor: React.FC<ServiceResourcesEditorProps> = ({
  resources,
  bookingType,
  subtype = '',
  onAdd,
  onRemove,
  onChange,
  unitPricing = false,
  unitDefaults,
}) => {
  const simultLabel = bookingType === 'day' ? 'Bookings at once (this unit, per day)' : 'Bookings at once (this unit, per slot)';
  const capacityLabel = unitCapacityWord(subtype);
  const unitWord = (UNIT_LABEL[subtype] || 'Units').replace(/s$/, '');
  const summary = summarizeResources(resources, subtype);

  return (
    <div className="text-sm">
      <p className="text-xs mb-3" style={{ color: 'var(--ink-500)' }}>
        One row per physical {unitWord.toLowerCase()}. Guests are matched to one at a time, so 5
        cabanas are 5 rows in this one service, not 5 services. Leave empty if the service has no
        separate units.
      </p>

      {summary && (
        <p className="text-xs font-medium mb-3" style={{ color: 'var(--ink-700)' }}>
          {summary}
        </p>
      )}

      {resources.length === 0 ? (
        <p className="text-xs italic mb-3" style={{ color: 'var(--ink-400)' }}>
          No extra resources — this service syncs as a single default resource, sized from Max
          Capacity / {bookingType === 'day' ? 'Daily Capacity' : 'Max Per Slot'} above.
        </p>
      ) : (
        <div className="space-y-2 mb-3">
          {resources.map((resource, index) => (
            <div
              key={resource.key || index}
              className="rounded-lg p-3"
              style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Boxes size={12} style={{ color: 'var(--ink-400)' }} />
                  <span className="text-[11px] font-mono" style={{ color: 'var(--ink-400)' }}>
                    key: {resource.key}
                  </span>
                </div>
                <button
                  type="button"
                  aria-label={`Remove resource ${resource.name || resource.key}`}
                  onClick={() => onRemove(index)}
                  className="rounded hover:opacity-70"
                  style={{ color: 'var(--ink-400)' }}
                >
                  <X size={13} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>Name</span>
                  <input
                    type="text"
                    value={resource.name}
                    onChange={(e) => onChange(index, 'name', e.target.value)}
                    placeholder={`e.g. ${unitWord} 3`}
                    className="rounded px-2 py-1 text-xs"
                    style={{ border: '1px solid var(--line)' }}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>{capacityLabel}</span>
                  <input
                    type="number"
                    min={0}
                    value={resource.capacity}
                    onChange={(e) => onChange(index, 'capacity', e.target.value)}
                    placeholder="e.g. 6"
                    className="rounded px-2 py-1 text-xs"
                    style={{ border: '1px solid var(--line)' }}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>{simultLabel}</span>
                  <input
                    type="number"
                    min={0}
                    value={resource.max_simultaneous_bookings}
                    onChange={(e) => onChange(index, 'max_simultaneous_bookings', e.target.value)}
                    placeholder="1"
                    className="rounded px-2 py-1 text-xs"
                    style={{ border: '1px solid var(--line)' }}
                  />
                </label>
              </div>
              {unitPricing && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {([
                    ['price', 'Price (override)', unitDefaults?.price],
                    ['included_pax', 'Included pax (override)', unitDefaults?.included_pax],
                    ['max_pax', 'Max pax (override)', unitDefaults?.max_pax],
                  ] as const).map(([field, label, fallback]) => (
                    <label key={field} className="flex flex-col gap-1">
                      <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>{label}</span>
                      <input
                        type="number"
                        min={0}
                        value={resource[field] ?? ''}
                        onChange={(e) => onChange(index, field, e.target.value)}
                        placeholder={fallback ? `default ${fallback}` : 'default'}
                        className="rounded px-2 py-1 text-xs"
                        style={{ border: '1px solid var(--line)' }}
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button type="button" onClick={onAdd} className="btn" style={{ width: '100%', justifyContent: 'center' }}>
        <Plus size={13} />
        Add {unitWord.toLowerCase()}
      </button>

      {subtype === 'teetime' && (
        <p className="text-[11px] mt-2" style={{ color: 'var(--ink-500)' }}>
          One unit per starting tee, not per course — split starts (e.g. groups off the 1st and
          the 10th) need two, otherwise the second slot at the same time can never be sold.
        </p>
      )}

      {resources.length > 0 && (
        <p className="text-[11px] mt-2" style={{ color: 'var(--ink-400)' }}>
          Every unit shares this service's opening hours and closures. Set prices under Pricing.
        </p>
      )}
    </div>
  );
};
